import React, { useState, useEffect, useMemo } from "react";
import { Upload, FileSpreadsheet, CheckCircle, AlertCircle, X, Info, Users, UserPlus, Eye, ArrowLeft, Download, History, AlertTriangle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "../components/ui/alert";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { apiRequest, API_BASE, formatDateTime } from "../lib/utils";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export const DataImportPage = () => {
  const { user, isAdmin } = useAuth();
  const [file, setFile] = useState(null);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [importType, setImportType] = useState("sdr");
  const [uploading, setUploading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [result, setResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [step, setStep] = useState(1);
  const [importHistory, setImportHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  // Get CRE's assigned branches
  const assignedBranches = useMemo(() => {
    if (isAdmin()) {
      return branches;
    }
    const creeBranchIds = [];
    if (user?.branch_id) creeBranchIds.push(user.branch_id);
    if (user?.branch_ids) creeBranchIds.push(...user.branch_ids);
    const uniqueIds = [...new Set(creeBranchIds)];
    return branches.filter(b => uniqueIds.includes(b.id));
  }, [branches, user, isAdmin]);

  useEffect(() => {
    fetchBranches();
    fetchImportHistory();
  }, []);

  useEffect(() => {
    if (!isAdmin() && assignedBranches.length > 0 && !selectedBranch) {
      setSelectedBranch(assignedBranches[0].id);
    }
  }, [assignedBranches, isAdmin, selectedBranch]);

  const fetchBranches = async () => {
    try {
      const data = await apiRequest("/branches");
      setBranches(data);
    } catch (error) {
      console.error("Failed to fetch branches:", error);
    }
  };

  const fetchImportHistory = async () => {
    try {
      const data = await apiRequest("/import/history?limit=10");
      setImportHistory(data);
    } catch (error) {
      console.error("Failed to fetch import history:", error);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (isValidFile(droppedFile)) {
        setFile(droppedFile);
        setPreviewData(null);
        setResult(null);
        setStep(1);
      } else {
        toast.error("Please upload an Excel (.xlsx, .xls) or CSV file");
      }
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (isValidFile(selectedFile)) {
        setFile(selectedFile);
        setPreviewData(null);
        setResult(null);
        setStep(1);
      } else {
        toast.error("Please upload an Excel (.xlsx, .xls) or CSV file");
      }
    }
  };

  const isValidFile = (file) => {
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv'
    ];
    return validTypes.includes(file.type) || 
           file.name.endsWith('.xlsx') || 
           file.name.endsWith('.xls') || 
           file.name.endsWith('.csv');
  };

  const handlePreview = async () => {
    if (!file) {
      toast.error("Please select a file to preview");
      return;
    }

    if (!selectedBranch && !isAdmin()) {
      toast.error("Please select a branch");
      return;
    }

    setPreviewing(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("branch_id", selectedBranch || "auto");
      formData.append("import_type", importType);
      formData.append("cre_id", user.id);

      const token = localStorage.getItem("token");
      const response = await fetch(`${API_BASE}/import/preview`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Preview failed");
      }

      const data = await response.json();
      
      if (data.error) {
        toast.error(data.error);
        setPreviewData({ ...data, hasError: true });
        setStep(2);
      } else {
        setPreviewData(data);
        setStep(2);
      }
    } catch (error) {
      toast.error(error.message || "Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const handleImport = async () => {
    if (!file || !previewData) {
      toast.error("Please preview the file first");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("branch_id", selectedBranch || "auto");
      formData.append("import_type", importType);
      formData.append("cre_id", user.id);

      const token = localStorage.getItem("token");
      const endpoint = importType === "sdr" ? "/import/sdr-customers" : "/import/leads";
      const response = await fetch(`${API_BASE}${endpoint}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Import failed");
      }

      const data = await response.json();
      setResult(data);
      setStep(3);
      fetchImportHistory();
      
      if (data.imported > 0 || data.merged > 0) {
        toast.success(`Import completed: ${data.imported} new, ${data.merged || 0} merged`);
      } else {
        toast.warning("No records were imported");
      }
    } catch (error) {
      toast.error(error.message || "Import failed");
      setResult({ error: error.message });
      setStep(3);
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    window.open(`${API_BASE}/import/template`, '_blank');
  };

  const clearFile = () => {
    setFile(null);
    setPreviewData(null);
    setResult(null);
    setStep(1);
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case "valid":
        return <Badge className="bg-green-100 text-green-800 text-xs">Valid</Badge>;
      case "duplicate":
        return <Badge className="bg-yellow-100 text-yellow-800 text-xs">Duplicate</Badge>;
      case "error":
        return <Badge className="bg-red-100 text-red-800 text-xs">Error</Badge>;
      default:
        return <Badge variant="outline" className="text-xs">{status}</Badge>;
    }
  };

  const getLifecycleBadge = (lifecycle) => {
    const styles = {
      due: "bg-yellow-100 text-yellow-800",
      overdue: "bg-orange-100 text-orange-800",
      delayed: "bg-blue-100 text-blue-800",
      no_show: "bg-red-100 text-red-800",
      active: "bg-green-100 text-green-800"
    };
    return <Badge className={`${styles[lifecycle] || "bg-gray-100 text-gray-800"} text-xs`}>
      {lifecycle?.replace("_", " ")}
    </Badge>;
  };

  const selectedBranchName = branches.find(b => b.id === selectedBranch)?.name || "Auto-create";

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="data-import-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl sm:text-2xl font-bold text-foreground">Data Import</h1>
          <p className="text-sm text-muted-foreground">
            {isAdmin() ? "Import SDR customers or leads" : "Import data for your branches"}
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={downloadTemplate}
            className="h-10"
            data-testid="download-template"
          >
            <Download className="w-4 h-4 mr-2" />
            Sample Template
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowHistory(!showHistory)}
            className="h-10"
          >
            <History className="w-4 h-4 mr-2" />
            History
          </Button>
        </div>
      </div>

      {/* Import History Panel */}
      {showHistory && (
        <Card className="bento-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <History className="w-4 h-4 text-[#ED1C24]" />
              Recent Imports
            </CardTitle>
          </CardHeader>
          <CardContent>
            {importHistory.length === 0 ? (
              <p className="text-sm text-muted-foreground">No import history yet</p>
            ) : (
              <div className="space-y-2">
                {importHistory.map((log) => (
                  <div key={log.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-gray-50 rounded-lg gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{log.filename}</p>
                      <p className="text-xs text-muted-foreground">{formatDateTime(log.upload_time)}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-xs">{log.total_rows} rows</Badge>
                      <Badge className="bg-green-100 text-green-800 text-xs">{log.success_count} success</Badge>
                      {log.failed_count > 0 && (
                        <Badge className="bg-red-100 text-red-800 text-xs">{log.failed_count} failed</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step Indicator - Mobile Optimized */}
      <div className="flex items-center justify-center gap-1 sm:gap-2 py-2">
        {[1, 2, 3].map((s) => (
          <div key={s} className="flex items-center gap-1 sm:gap-2">
            <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-medium ${
              step >= s ? "bg-[#ED1C24] text-white" : "bg-gray-200 text-gray-500"
            }`}>
              {s}
            </div>
            <span className={`text-xs hidden sm:inline ${step >= s ? "text-foreground" : "text-muted-foreground"}`}>
              {s === 1 ? "Upload" : s === 2 ? "Preview" : "Done"}
            </span>
            {s < 3 && <div className={`w-6 sm:w-8 h-0.5 ${step > s ? "bg-[#ED1C24]" : "bg-gray-200"}`}></div>}
          </div>
        ))}
      </div>

      {/* CRE Branch Restriction Notice */}
      {!isAdmin() && (
        <Alert className="bg-blue-50 border-blue-200">
          <Info className="h-4 w-4 text-blue-600 flex-shrink-0" />
          <AlertDescription className="text-blue-800 text-sm">
            Import for: {assignedBranches.map(b => b.name).join(", ")}
          </AlertDescription>
        </Alert>
      )}

      {/* Step 1: Upload */}
      {step === 1 && (
        <div className="space-y-4">
          <Card className="bento-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Upload className="w-4 h-4 text-[#ED1C24]" />
                Upload File
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Import Type Tabs */}
              <Tabs value={importType} onValueChange={setImportType} className="w-full">
                <TabsList className="grid w-full grid-cols-2 h-11">
                  <TabsTrigger value="sdr" className="text-sm h-10" data-testid="import-type-sdr">
                    <Users className="w-4 h-4 mr-2" />
                    SDR Customers
                  </TabsTrigger>
                  <TabsTrigger value="leads" className="text-sm h-10" data-testid="import-type-leads">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Leads
                  </TabsTrigger>
                </TabsList>
              </Tabs>

              {/* Drop Zone */}
              <div
                className={`relative border-2 border-dashed rounded-xl p-6 sm:p-8 text-center transition-colors ${
                  dragActive 
                    ? "border-[#ED1C24] bg-red-50" 
                    : file 
                      ? "border-green-500 bg-green-50" 
                      : "border-gray-300 hover:border-gray-400"
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileChange}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  data-testid="file-input"
                />
                
                {file ? (
                  <div className="flex flex-col items-center gap-2">
                    <FileSpreadsheet className="w-10 h-10 sm:w-12 sm:h-12 text-green-600" />
                    <p className="font-medium text-green-800 text-sm sm:text-base break-all px-2">{file.name}</p>
                    <p className="text-xs text-green-600">{(file.size / 1024).toFixed(1)} KB</p>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={(e) => { e.stopPropagation(); clearFile(); }}
                      className="text-red-600 hover:text-red-700 h-9"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Remove
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <Upload className={`w-10 h-10 sm:w-12 sm:h-12 ${dragActive ? "text-[#ED1C24]" : "text-gray-400"}`} />
                    <p className="font-medium text-sm sm:text-base">Drop file here</p>
                    <p className="text-xs text-muted-foreground">or tap to browse</p>
                    <p className="text-xs text-muted-foreground mt-1">.xlsx, .xls, .csv</p>
                  </div>
                )}
              </div>

              {/* Branch Selection */}
              <div>
                <label className="text-sm font-medium block mb-1">Branch *</label>
                <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                  <SelectTrigger className="h-11" data-testid="branch-select">
                    <SelectValue placeholder="Select a branch" />
                  </SelectTrigger>
                  <SelectContent>
                    {isAdmin() && <SelectItem value="auto">Auto-create test branch</SelectItem>}
                    {assignedBranches.map((branch) => (
                      <SelectItem key={branch.id} value={branch.id}>
                        {branch.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Preview Button */}
              <Button 
                onClick={handlePreview} 
                disabled={!file || previewing || (!selectedBranch && !isAdmin())}
                className="w-full h-12 bg-[#ED1C24] hover:bg-[#C41219] text-base"
                data-testid="preview-button"
              >
                {previewing ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <Eye className="w-5 h-5 mr-2" />
                    Preview Data
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Expected Columns Card */}
          <Card className="bento-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Info className="w-4 h-4 text-blue-600" />
                Expected Columns
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
                <div className="p-2 bg-gray-50 rounded text-center">
                  <span className="font-medium">DATE</span>
                  <p className="text-xs text-muted-foreground">Service date</p>
                </div>
                <div className="p-2 bg-gray-50 rounded text-center">
                  <span className="font-medium">Name</span>
                  <p className="text-xs text-muted-foreground">Customer</p>
                </div>
                <div className="p-2 bg-green-50 border border-green-200 rounded text-center">
                  <span className="font-medium text-green-800">Number*</span>
                  <p className="text-xs text-green-600">Required</p>
                </div>
                <div className="p-2 bg-gray-50 rounded text-center">
                  <span className="font-medium">Vehicle</span>
                  <p className="text-xs text-muted-foreground">Model</p>
                </div>
                <div className="p-2 bg-gray-50 rounded text-center">
                  <span className="font-medium">RG NOM</span>
                  <p className="text-xs text-muted-foreground">Reg number</p>
                </div>
                <div className="p-2 bg-gray-50 rounded text-center">
                  <span className="font-medium">CATEGORY</span>
                  <p className="text-xs text-muted-foreground">Status</p>
                </div>
              </div>

              <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="text-xs text-yellow-800">
                  <AlertTriangle className="w-3 h-3 inline mr-1" />
                  Duplicates (same phone) will be auto-skipped
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Step 2: Preview */}
      {step === 2 && previewData && (
        <Card className="bento-card">
          <CardHeader className="pb-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Eye className="w-4 h-4 text-[#ED1C24]" />
                Preview ({previewData.total_rows} rows)
              </CardTitle>
              <Button variant="outline" size="sm" onClick={() => setStep(1)} className="h-9">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Column Detection Info */}
            {previewData.column_mapping && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-xs text-blue-800 font-medium mb-1">Detected Columns:</p>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(previewData.column_mapping).map(([key, val]) => (
                    <Badge key={key} variant="outline" className="text-xs">
                      {key}: {val}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Summary Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="p-3 rounded-lg bg-gray-50 border text-center">
                <p className="text-xs text-muted-foreground">Total</p>
                <p className="text-xl font-bold">{previewData.total_rows}</p>
              </div>
              <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-center">
                <p className="text-xs text-green-700">Valid</p>
                <p className="text-xl font-bold text-green-700">{previewData.valid_count}</p>
              </div>
              <div className="p-3 rounded-lg bg-yellow-50 border border-yellow-200 text-center">
                <p className="text-xs text-yellow-700">Duplicates</p>
                <p className="text-xl font-bold text-yellow-700">{previewData.duplicate_count}</p>
              </div>
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-center">
                <p className="text-xs text-red-700">Errors</p>
                <p className="text-xl font-bold text-red-700">{previewData.error_count}</p>
              </div>
            </div>

            {/* Preview Cards (Mobile) / Table (Desktop) */}
            <div className="max-h-[50vh] overflow-y-auto border rounded-lg">
              {/* Mobile Card View */}
              <div className="sm:hidden space-y-2 p-2">
                {previewData.rows?.slice(0, 30).map((row, idx) => (
                  <div 
                    key={idx} 
                    className={`p-3 rounded-lg border ${
                      row.status === "error" ? "bg-red-50 border-red-200" : 
                      row.status === "duplicate" ? "bg-yellow-50 border-yellow-200" : 
                      "bg-white"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{row.name || "-"}</p>
                        <p className="font-mono text-xs text-muted-foreground">{row.phone || "-"}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        {getStatusBadge(row.status)}
                        {row.lifecycle && getLifecycleBadge(row.lifecycle)}
                      </div>
                    </div>
                    {row.notes && (
                      <p className="text-xs text-red-600 mt-1">{row.notes}</p>
                    )}
                    <div className="flex gap-2 mt-2 text-xs text-muted-foreground">
                      {row.vehicle && <span>🚗 {row.vehicle}</span>}
                      {row.last_service_date && <span>📅 {row.last_service_date}</span>}
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View */}
              <table className="hidden sm:table w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="p-2 text-left font-medium">Row</th>
                    <th className="p-2 text-left font-medium">Status</th>
                    <th className="p-2 text-left font-medium">Name</th>
                    <th className="p-2 text-left font-medium">Phone</th>
                    <th className="p-2 text-left font-medium">Vehicle</th>
                    <th className="p-2 text-left font-medium">Date</th>
                    <th className="p-2 text-left font-medium">Category</th>
                    <th className="p-2 text-left font-medium">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.rows?.slice(0, 50).map((row, idx) => (
                    <tr key={idx} className={`border-t ${row.status === "error" ? "bg-red-50" : row.status === "duplicate" ? "bg-yellow-50" : ""}`}>
                      <td className="p-2 text-muted-foreground">{row.row_num}</td>
                      <td className="p-2">{getStatusBadge(row.status)}</td>
                      <td className="p-2">{row.name || "-"}</td>
                      <td className="p-2 font-mono text-xs">{row.phone || "-"}</td>
                      <td className="p-2">{row.vehicle || "-"}</td>
                      <td className="p-2">{row.last_service_date || "-"}</td>
                      <td className="p-2">{row.lifecycle ? getLifecycleBadge(row.lifecycle) : "-"}</td>
                      <td className="p-2 text-xs text-muted-foreground max-w-[150px] truncate">{row.notes || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {previewData.rows?.length > 50 && (
                <p className="text-center py-2 text-xs text-muted-foreground bg-gray-50">
                  Showing 50 of {previewData.rows.length} rows
                </p>
              )}
            </div>

            {/* Error Alert */}
            {previewData.hasError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Column Detection Issue</AlertTitle>
                <AlertDescription className="text-sm">
                  {previewData.error}
                </AlertDescription>
              </Alert>
            )}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button variant="outline" onClick={() => setStep(1)} className="h-11 sm:flex-1">
                Cancel
              </Button>
              <Button 
                onClick={handleImport} 
                disabled={uploading || previewData.valid_count === 0 || previewData.hasError}
                className="h-11 sm:flex-1 bg-[#ED1C24] hover:bg-[#C41219]"
                data-testid="confirm-import-button"
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Importing...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Import {previewData.valid_count} Records
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Results */}
      {step === 3 && result && (
        <Card className="bento-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              {result.error ? (
                <AlertCircle className="w-5 h-5 text-red-600" />
              ) : (
                <CheckCircle className="w-5 h-5 text-green-600" />
              )}
              Import {result.error ? "Failed" : "Complete"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {result.error ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Import Failed</AlertTitle>
                <AlertDescription>{result.error}</AlertDescription>
              </Alert>
            ) : (
              <div className="space-y-4">
                {/* Summary Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div className="p-3 rounded-lg bg-gray-50 border text-center">
                    <p className="text-xs text-muted-foreground">Total</p>
                    <p className="text-xl font-bold">{result.total_rows}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-center">
                    <p className="text-xs text-green-700">Imported</p>
                    <p className="text-xl font-bold text-green-700">{result.imported}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-center">
                    <p className="text-xs text-blue-700">Merged</p>
                    <p className="text-xl font-bold text-blue-700">{result.merged || 0}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-yellow-50 border border-yellow-200 text-center">
                    <p className="text-xs text-yellow-700">Skipped</p>
                    <p className="text-xl font-bold text-yellow-700">{result.skipped || 0}</p>
                  </div>
                </div>

                {/* Lifecycle Summary */}
                {result.lifecycle_summary && (
                  <div>
                    <p className="text-sm font-medium mb-2">Distribution:</p>
                    <div className="grid grid-cols-5 gap-1 text-center">
                      {Object.entries(result.lifecycle_summary).map(([key, val]) => (
                        <div key={key} className="p-2 bg-gray-50 rounded">
                          <p className="text-xs text-muted-foreground capitalize">{key.replace("_", " ")}</p>
                          <p className="font-bold">{val}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Errors List */}
                {result.errors && result.errors.length > 0 && (
                  <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                    <p className="text-sm font-medium text-red-800 mb-2">
                      Errors ({result.errors.length}):
                    </p>
                    <div className="max-h-24 overflow-y-auto space-y-1">
                      {result.errors.slice(0, 10).map((err, idx) => (
                        <p key={idx} className="text-xs text-red-700">{err}</p>
                      ))}
                      {result.errors.length > 10 && (
                        <p className="text-xs text-muted-foreground">
                          ... and {result.errors.length - 10} more
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Success Message */}
                {(result.imported > 0 || result.merged > 0) && (
                  <Alert className="bg-green-50 border-green-200">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-700 text-sm">
                      Success! Check Due, Overdue, Delayed, No Show pages.
                    </AlertDescription>
                  </Alert>
                )}

                {/* Import Another */}
                <Button onClick={clearFile} variant="outline" className="w-full h-11">
                  <Upload className="w-4 h-4 mr-2" />
                  Import Another File
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DataImportPage;
